import { type ExtractPropTypes, computed, useSlots } from 'vue';

import { withTextClipProps, storiesTextClipArgs } from '../../composables/props/textClip';
import { BreakpointDepended } from '../breakpointDepended';
import { defaults } from '../defaults';
import { withButtonVariantProps, useButtonVariantProps, storiesButtonVariantArgs } from '../props/buttonVariant';
import { withColorProps, useColorProps, storiesColorArgs } from '../props/colors';
import { withDisabledProps, useDisabledProps, storiesDisabledArgs } from '../props/disabled';
import { withFluidProps, useFluidProps, storiesFluidArgs } from '../props/fluid';
import { withIconProps, useIconProps, storiesIconArgs } from '../props/icon';
import { withIconPositionProps, storiesIconPositionArgs } from '../props/iconPosition';
import { withJustifyProps, useJustifyProps, storiesJusitfyArgs } from '../props/justify';
import { withLoadingProps, useLoadingProps, storiesLoadingArgs } from '../props/loading';
import { withRadiusProps, useRadiusProps, storiesRadiusArgs } from '../props/radius';
import { withRouterActivityProps, useRouterActivityProps, storiesRouterActivityArgs } from '../props/routerActivity';
import { withSizeProps, useSizeProps, storiesSizeArgs } from '../props/size';
import { storiesTextAlignArgs, useTextAlignProps, withTextAlignProps } from '../props/textAlign';

export const withButtonProps = {
  ...withColorProps(defaults.button.color),
  ...withSizeProps(defaults.button.size),
  ...withJustifyProps(defaults.button.justify),
  ...withTextAlignProps(defaults.button.textAlign),
  ...withFluidProps(defaults.button.fluid),
  ...withRouterActivityProps(defaults.button.routerActivity),
  ...withTextClipProps(defaults.button.textClip),
  ...withIconProps(),
  ...withIconPositionProps,
  ...withButtonVariantProps,
  ...withRadiusProps,
  ...withLoadingProps,
  ...withDisabledProps,

  /** Treść przycisku */
  text: {
    type: String
  },

  /** Label przycisku */
  label: {
    type: String
  },

  /** Adres lub obiekt do zbudowania router-link */
  to: {
    type: [Object, String]
  },

  /** Link do przekierowania */
  href: {
    type: String
  },

  /** Target linku */
  target: {
    type: String
  },

  /** Czy wyświetlać tekst jako HTML */
  htmlText: {
    type: Boolean
  },

  /** Atrybut "Type" przycisku */
  type: {
    type: String,
    default: defaults.button.type
  }
};

export function useButtonProps(prefix: string, props: ExtractPropTypes<typeof withButtonProps>) {
  const buttonVariantProps = useButtonVariantProps(prefix, props);
  const colorProps = useColorProps(prefix, props);
  const fluidProps = useFluidProps(prefix, props);
  const iconProps = useIconProps(props);
  const loadingProps = useLoadingProps(props);
  const radiusProps = useRadiusProps(prefix, props);
  const sizeProps = useSizeProps(prefix, props);
  const justifyProps = useJustifyProps(prefix, props);
  const disabledProps = useDisabledProps(prefix, props);
  const routerActivityProps = useRouterActivityProps(prefix, props);
  const textAlignProps = useTextAlignProps(props);

  /** Wyliczone klasy przycisku */
  const componentClasses = computed(() => {
    const slots = useSlots();

    return [
      radiusProps.value,
      buttonVariantProps.value,
      colorProps.value,
      sizeProps.value,
      fluidProps.value,
      justifyProps.value,
      disabledProps.value,
      routerActivityProps.value,
      textAlignProps.value,

      { 'w-btn--has-icon': props.icon },
      {
        'w-btn--only-icon': props.icon && !props.text && !(slots.default || slots.beforeContent || slots.afterContent)
      }
    ];
  });

  const iconSize = computed(() => {
    if (typeof props.size === 'string') {
      return defaults.button.iconSize[props.size];
    }

    const breakpointValue = BreakpointDepended(props.size).value;

    if (breakpointValue) {
      return defaults.button.iconSize[breakpointValue];
    }

    return undefined;
  });

  return {
    componentClasses,
    iconProps,
    iconSize,
    loadingProps
  };
}

/**
 * Konfiguracja dla storybooka
 */
export const storiesButtonArgs = {
  args: {
    text: 'Przycisk',
    label: '',
    href: '',
    target: '',
    type: defaults.button.type,
    textClip: defaults.button.textClip,
    ...storiesButtonVariantArgs.args,
    ...storiesColorArgs.args,
    ...storiesDisabledArgs.args,
    ...storiesFluidArgs.args,
    ...storiesIconPositionArgs.args,
    ...storiesIconArgs.args,
    ...storiesJusitfyArgs.args,
    ...storiesLoadingArgs.args,
    ...storiesRadiusArgs.args,
    ...storiesSizeArgs.args,
    ...storiesRouterActivityArgs.args,
    ...storiesTextAlignArgs.args
  },

  argTypes: {
    text: {
      control: { type: 'text' }
    },
    label: {
      control: { type: 'text' }
    },
    href: {
      control: { type: 'text' }
    },
    target: {
      control: { type: 'text' }
    },
    type: {
      control: { type: 'text' }
    },

    ...storiesTextClipArgs.argTypes,
    ...storiesButtonVariantArgs.argTypes,
    ...storiesColorArgs.argTypes,
    ...storiesDisabledArgs.argTypes,
    ...storiesFluidArgs.argTypes,
    ...storiesIconPositionArgs.argTypes,
    ...storiesIconArgs.argTypes,
    ...storiesJusitfyArgs.argTypes,
    ...storiesLoadingArgs.argTypes,
    ...storiesRadiusArgs.argTypes,
    ...storiesSizeArgs.argTypes,
    ...storiesRouterActivityArgs.argTypes,
    ...storiesTextAlignArgs.argTypes
  }
};
